import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { WorkflowManagerConfigType } from "generated-graphql/graphql";
import { useCallback } from "react";

import { WORKFLOW_MANAGER_CONFIG_RULES_QUERY } from "../Rules";

const CREATE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION = gql(`
  mutation CreateWorkflowManagerConfigRule($type: WorkflowManagerConfigType!, $value: String!, $notes: String) {
    createWorkflowManagerConfigRule(type: $type, value: $value, notes: $notes)
  }
`);

interface UseCreateRuleMutationOptions {
  onSuccess?: () => void;
}

/**
 * Hook to handle creating workflow manager config rules with optimistic updates
 */
export function useCreateRuleMutation({ onSuccess }: UseCreateRuleMutationOptions = {}) {
  const alerts = useAlerts();

  const [createMutation, { loading }] = useMutation(CREATE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION, {
    optimisticResponse: {
      createWorkflowManagerConfigRule: true,
    },
    update: (cache, _result, options) => {
      // Optimistically update the cache by reading current data and adding a temporary rule
      // The refetch will replace this with the real data
      try {
        const existingData = cache.readQuery({
          query: WORKFLOW_MANAGER_CONFIG_RULES_QUERY,
        });

        if (existingData && options.variables) {
          const transformedValue = options.variables.value as string;

          // Create a temporary optimistic rule
          const tempId = `temp-${Date.now()}`;
          const tempRule = {
            __typename: "WorkflowManagerConfigRule" as const,
            id: tempId,
            type: options.variables.type as WorkflowManagerConfigType,
            value: transformedValue,
            humanReadableValue: transformedValue,
            notes: (options.variables.notes as string | null) || null,
            createdAt: new Date().toISOString(),
            createdBy: "current user",
            updatedAt: new Date().toISOString(),
            updatedBy: "current user",
            facility: null,
            tenant: null,
          };

          cache.writeQuery({
            query: WORKFLOW_MANAGER_CONFIG_RULES_QUERY,
            data: {
              ...existingData,
              workflowManagerConfigRules: {
                ...existingData.workflowManagerConfigRules,
                items: [tempRule, ...existingData.workflowManagerConfigRules.items],
                count: existingData.workflowManagerConfigRules.count + 1,
              },
            },
          });
        }
      } catch (error) {
        console.warn("Failed to optimistically update cache:", error);
      }
    },
    refetchQueries: [WORKFLOW_MANAGER_CONFIG_RULES_QUERY],
    onCompleted: () => {
      alerts.success("Rule added successfully");
      onSuccess?.();
    },
    onError: (error) => {
      alerts.error(`Failed to add rule: ${error.message}`);
    },
  });

  const createRule = useCallback(
    (type: WorkflowManagerConfigType, value: string, notes: string | null) => {
      createMutation({
        variables: {
          type,
          value,
          notes: notes?.trim() || null,
        },
      });
    },
    [createMutation]
  );

  return {
    createRule,
    loading,
  };
}
